import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { marked } from 'marked';
import { Message, ChatEvent, AiChatProps } from './types.js';

@customElement('ai-chat')
export class AiChat extends LitElement {
  @property({ type: Array }) messages: Message[] = [];
  @property({ type: Boolean }) loading = false;
  @property({ type: String }) placeholder = 'Type a message...';
  @property({ type: String }) maxHeight = '400px';
  @property({ type: String }) theme: 'light' | 'dark' = 'light';
  @property({ type: Boolean }) showTimestamp = true;
  @property({ type: Boolean }) enableMarkdown = true;

  @state() private inputValue = '';

  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      --primary-color: #007AFF;
      --background-color: #FFFFFF;
      --text-color: #000000;
      --border-color: #E5E5E7;
      --user-message-bg: #007AFF;
      --assistant-message-bg: #F2F2F7;
      --shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    :host([theme="dark"]) {
      --background-color: #1C1C1E;
      --text-color: #FFFFFF;
      --border-color: #38383A;
      --user-message-bg: #007AFF;
      --assistant-message-bg: #2C2C2E;
    }

    .chat-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: var(--max-height, 400px);
      background-color: var(--background-color);
      border: 1px solid var(--border-color);
      border-radius: 12px;
      overflow: hidden;
      box-shadow: var(--shadow);
    }

    .messages-container {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      -webkit-overflow-scrolling: touch;
    }

    .message {
      display: flex;
      flex-direction: column;
      max-width: 85%;
      word-wrap: break-word;
    }

    .message.user {
      align-self: flex-end;
    }

    .message.assistant {
      align-self: flex-start;
    }

    .message-content {
      padding: 12px 16px;
      border-radius: 18px;
      font-size: 16px;
      line-height: 1.4;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .message.user .message-content {
      background-color: var(--user-message-bg);
      color: white;
      border-bottom-right-radius: 4px;
    }

    .message.assistant .message-content {
      background-color: var(--assistant-message-bg);
      color: var(--text-color);
      border-bottom-left-radius: 4px;
    }

    .message-timestamp {
      font-size: 12px;
      color: #8E8E93;
      margin-top: 4px;
      text-align: right;
    }

    .message.assistant .message-timestamp {
      text-align: left;
    }

    .input-container {
      display: flex;
      padding: 16px;
      background-color: var(--background-color);
      border-top: 1px solid var(--border-color);
      gap: 8px;
    }

    .input-field {
      flex: 1;
      padding: 12px 16px;
      border: 1px solid var(--border-color);
      border-radius: 20px;
      font-size: 16px;
      background-color: var(--background-color);
      color: var(--text-color);
      outline: none;
      resize: none;
      min-height: 20px;
      max-height: 100px;
      font-family: inherit;
    }

    .input-field:focus {
      border-color: var(--primary-color);
    }

    .input-field::placeholder {
      color: #8E8E93;
    }

    .send-button {
      padding: 12px 20px;
      background-color: var(--primary-color);
      color: white;
      border: none;
      border-radius: 20px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s;
      min-width: 60px;
    }

    .send-button:hover:not(:disabled) {
      opacity: 0.8;
    }

    .send-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .loading-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      color: #8E8E93;
      font-size: 14px;
    }

    .loading-dots {
      display: flex;
      gap: 4px;
    }

    .loading-dot {
      width: 6px;
      height: 6px;
      background-color: #8E8E93;
      border-radius: 50%;
      animation: loading 1.4s infinite ease-in-out;
    }

    .loading-dot:nth-child(1) { animation-delay: -0.32s; }
    .loading-dot:nth-child(2) { animation-delay: -0.16s; }

    @keyframes loading {
      0%, 80%, 100% {
        transform: scale(0);
      }
      40% {
        transform: scale(1);
      }
    }

    .markdown-content {
      line-height: 1.6;
    }

    .markdown-content h1,
    .markdown-content h2,
    .markdown-content h3,
    .markdown-content h4,
    .markdown-content h5,
    .markdown-content h6 {
      margin: 16px 0 8px 0;
      font-weight: 600;
    }

    .markdown-content p {
      margin: 8px 0;
    }

    .markdown-content code {
      background-color: rgba(0, 0, 0, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
      font-size: 14px;
    }

    .markdown-content pre {
      background-color: rgba(0, 0, 0, 0.05);
      padding: 12px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 12px 0;
    }

    .markdown-content pre code {
      background: none;
      padding: 0;
    }

    .markdown-content ul,
    .markdown-content ol {
      margin: 8px 0;
      padding-left: 20px;
    }

    .markdown-content li {
      margin: 4px 0;
    }

    .markdown-content blockquote {
      border-left: 4px solid var(--primary-color);
      margin: 12px 0;
      padding-left: 16px;
      color: #8E8E93;
    }

    .markdown-content a {
      color: var(--primary-color);
      text-decoration: none;
    }

    .markdown-content a:hover {
      text-decoration: underline;
    }

    /* Mobile optimizations */
    @media (max-width: 768px) {
      .message {
        max-width: 90%;
      }

      .message-content {
        font-size: 15px;
        padding: 10px 14px;
      }

      .input-container {
        padding: 12px;
      }

      .input-field {
        font-size: 16px; /* Prevents zoom on iOS */
      }

      .messages-container {
        padding: 12px;
      }
    }

    @media (max-width: 480px) {
      .message {
        max-width: 95%;
      }

      .message-content {
        font-size: 14px;
        padding: 8px 12px;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--max-height', this.maxHeight);
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('maxHeight')) {
      this.style.setProperty('--max-height', this.maxHeight);
    }
    if (changedProperties.has('messages')) {
      this.scrollToBottom();
    }
  }

  private scrollToBottom() {
    const messagesContainer = this.shadowRoot?.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  private formatTimestamp(timestamp?: number): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private renderMessageContent(message: Message) {
    if (message.type === 'markdown' && this.enableMarkdown) {
      const htmlContent = marked(message.content);
      return html`<div class="markdown-content" .innerHTML="${htmlContent}"></div>`;
    }
    return html`<div>${message.content}</div>`;
  }

  private handleInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.inputValue = target.value;
    
    // Auto-resize textarea
    target.style.height = 'auto';
    target.style.height = Math.min(target.scrollHeight, 100) + 'px';
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private sendMessage() {
    if (!this.inputValue.trim() || this.loading) return;

    const message: Message = {
      id: Date.now().toString(),
      type: 'text',
      content: this.inputValue.trim(),
      timestamp: Date.now(),
      sender: 'user'
    };

    this.dispatchEvent(new CustomEvent<ChatEvent>('message-sent', {
      detail: {
        type: 'message',
        data: message,
        messageId: message.id
      }
    }));

    this.inputValue = '';
    const textarea = this.shadowRoot?.querySelector('.input-field') as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
    }
  }

  private renderMessages() {
    return this.messages.map(message => {
      const sender = message.sender || 'assistant';
      return html`
        <div class="message ${sender}">
          <div class="message-content">
            ${this.renderMessageContent(message)}
          </div>
          ${this.showTimestamp && message.timestamp ? html`
            <div class="message-timestamp">
              ${this.formatTimestamp(message.timestamp)}
            </div>
          ` : ''}
        </div>
      `;
    });
  }

  private renderLoadingIndicator() {
    if (!this.loading) return '';
    
    return html`
      <div class="message assistant">
        <div class="message-content">
          <div class="loading-indicator">
            <span>AI is typing</span>
            <div class="loading-dots">
              <div class="loading-dot"></div>
              <div class="loading-dot"></div>
              <div class="loading-dot"></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="chat-container">
        <div class="messages-container">
          ${this.renderMessages()}
          ${this.renderLoadingIndicator()}
        </div>
        <div class="input-container">
          <textarea
            class="input-field"
            .value="${this.inputValue}"
            .placeholder="${this.placeholder}"
            ?disabled="${this.loading}"
            @input="${this.handleInput}"
            @keydown="${this.handleKeyDown}"
            rows="1"
          ></textarea>
          <button
            class="send-button"
            ?disabled="${!this.inputValue.trim() || this.loading}"
            @click="${this.sendMessage}"
          >
            Send
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-chat': AiChat;
  }
}